import { head, first } from 'lodash'
import React, { ReactElement, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { Applicant, CreditApplication } from '../../data/schemas/PrequalificationSchema'
import { FormatCurrency, formatPhone } from '../utils/Formatter'
import { useAppSelector } from '../../data/store'
import { creditApplicationEffects, creditApplicationSelectors } from '../../data/store/CreditApplicationStore'
import { ValidatePromotionCode, PromotionCodeDto } from '../../data/types/PromoCode'
import { Enum } from '../../data/types/constants'
import { useSideEffect } from '../../data/store/Effects/side-effects'

type Props = {
  gotoStep: (step: string) => void
  isCoapplicant?: boolean
  stepProgression: ReactElement
  creditApplication: CreditApplication
}

function Review({ isCoapplicant, stepProgression, creditApplication, gotoStep }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const [isSubmitted, setSubmitted] = React.useState<boolean>(false)
  const isValidatingPromoCode = useAppSelector(creditApplicationSelectors.validatePromoCodeLoading)
  const merchantHasPromotion = useAppSelector(creditApplicationSelectors.getMerchantSupportsPromotions)
  const [promoCodeInput, setPromoCodeInput] = React.useState<string>('')
  const [promoCodeValidation, setPromoValidation] = React.useState<ValidatePromotionCode | null>(null)
  const isPromoCodeValidated = promoCodeValidation?.status === Enum.EPromoCodeStatus.Valid
  const shoudDisplayPromoCodeInput = !isPromoCodeValidated || promoCodeInput.length > 0
  const shouldDisplayPromoCodeReviewInfo = !shoudDisplayPromoCodeInput
  const shouldDisplayAnyPromoCodeRelatedInfo = merchantHasPromotion && !isCoapplicant
  const dispatchEffect = useSideEffect()
  const currentApplicant = isCoapplicant ? creditApplication.coApplicant : creditApplication.applicant
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const submitPrequalification = useCallback(
    (evt: { preventDefault: () => void }) => {
      evt.preventDefault()
      setIsSubmitting(true)
      const creditApplicationData = {
        ...creditApplication,
        merchantPaymentPlanId: promoCodeValidation?.merchantPaymentPlanId ?? null,
      }

      if (isCoapplicant) {
        dispatchEffect(
          creditApplicationEffects.postCoapplicant(creditApplicationData.coApplicant as Applicant, id!),
        ).then(() => {
          setSubmitted(true)
          setIsSubmitting(false)
          navigate(`/creditApplication/${id}`, { replace: true })
        })
      } else {
        dispatchEffect(creditApplicationEffects.postDraftCreditApplicationAndPrequalify(creditApplicationData)).then(
          (appRet) => {
            setIsSubmitting(false)
            navigate(`/creditApplication/${appRet.id}`, { replace: true })
          },
        )
      }
    },
    [promoCodeValidation, creditApplication],
  )

  const validatePromoCode = React.useCallback(
    (promoCode: string) => {
      const alphanumericRegex = /^[a-zA-Z0-9]+$/

      if (!alphanumericRegex.test(promoCode)) {
        setPromoValidation(null)
        return
      }

      const promoCodeDto: PromotionCodeDto = {
        promotionCode: promoCode,
        merchantId: creditApplication.merchantId,
      } as PromotionCodeDto
      dispatchEffect(creditApplicationEffects.validatePromotionCode(promoCodeDto)).then(
        (response: ValidatePromotionCode) => {
          setPromoValidation(response)
        },
      )
    },
    [creditApplication?.merchantId],
  )

  const getPromoError = () => {
    if (!promoCodeValidation) return ''

    if (promoCodeValidation.status === Enum.EPromoCodeStatus.Valid)
      return (
        <div className="text-success">
          {t<string>('loanAmount.validPromo')}&nbsp;
          <i className="fa-regular fa-check" />
        </div>
      )
    if (promoCodeValidation?.status === Enum.EPromoCodeStatus.Expired)
      return <div className="error-message">{t<string>('loanAmount.expiredPromo')}</div>
    if (promoCodeValidation?.status === Enum.EPromoCodeStatus.Invalid)
      return <div className="error-message">{t<string>('loanAmount.invalidPromo')}</div>
    return ''
  }

  const getValidatePromotionButtonText = () => {
    if (isValidatingPromoCode)
      return (
        <>
          {t<string>('loanAmount.validatingPromo')}
          <div className="mini-spinner" />
        </>
      )

    return t<string>('loanAmount.validatePromo')
  }

  const onPromoCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPromoCodeInput(e.target.value)
    if (promoCodeValidation && promoCodeValidation.status !== Enum.EPromoCodeStatus.Valid) setPromoValidation(null)
  }

  const merchantIsIfinanceMedicard =
    creditApplication.merchantId === `alis*${process.env.REACT_APP_IFINANCE_MERCHANT_ID}` ||
    creditApplication.merchantId === `alis*${process.env.REACT_APP_MEDICARD_MERCHANT_ID}`

  return (
    <main className="form-section step-8-active">
      {stepProgression}

      <section className="step-content">
        <h1>{t<string>('review.title')}</h1>
        <div className="paragraph">
          <p>{t<string>('review.description')}</p>
        </div>

        <form onSubmit={submitPrequalification}>
          <div className="review-wrap">
            {!isCoapplicant && (
              <>
                <div className="review-item">
                  <h1 className="h3">{t<string>('review.desiredLoanAmount')}</h1>
                  <div className="box">
                    <div className="box-content">
                      <p>
                        <strong>{t('loanAmount.amount')} : </strong>
                        <span>{FormatCurrency(creditApplication.requestedLoanAmount)}</span>
                      </p>
                    </div>
                    <button type="button" className="btn-action" onClick={() => gotoStep('loan-amount')}>
                      {t<string>('review.modify')}
                    </button>
                  </div>
                </div>

                {merchantIsIfinanceMedicard && (
                  <div className="review-item">
                    <h1 className="h3">{t<string>('common.loanPurpose')}</h1>
                    <div className="box">
                      <div className="box-content">
                        <p>
                          <strong>{t('common.loanPurpose')} : </strong>
                          <span>{t<string>(`enum.eLoanPurpose.${creditApplication.loanPurposeId}`)}</span>
                        </p>
                        <p>
                          <strong>{t('common.serviceProvider')} : </strong>
                          <span>{creditApplication.merchantName}</span>
                        </p>
                      </div>
                      <button type="button" className="btn-action" onClick={() => gotoStep('loan-purpose')}>
                        {t<string>('review.modify')}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="review-item">
              <h1 className="h3">
                {isCoapplicant ? t<string>('common.aboutCoapplicant') : t<string>('common.aboutYourself')}
              </h1>
              <div className="box">
                <div className="box-content">
                  <p>
                    <strong>{t<string>('personalInformation.yourGender')} : </strong>
                    <span>{t<string>(`enum.eGenderType.${currentApplicant?.genderId}`)}</span>
                  </p>
                  <p>
                    <strong>{t('personalInformation.yourFirstName')} : </strong>
                    <span>{currentApplicant?.firstName}</span>
                  </p>
                  <p>
                    <strong>{t('personalInformation.yourLastName')} : </strong>
                    <span>{currentApplicant?.lastName}</span>
                  </p>
                  <p>
                    <strong>{t('confirmation.birthDate')} : </strong>
                    <span>{currentApplicant?.birthDate}</span>
                  </p>
                  <p>
                    <strong>{t('confirmation.mobilePhone')} : </strong>
                    <span>{formatPhone(currentApplicant?.cellPhone)}</span>
                  </p>
                  {currentApplicant?.homePhone && (
                    <p>
                      <strong>{t('confirmation.homePhone')} : </strong>
                      <span>{formatPhone(currentApplicant?.homePhone)}</span>
                    </p>
                  )}
                  <p>
                    <strong>{t('confirmation.email')} : </strong>
                    <span>{currentApplicant?.email}</span>
                  </p>
                  {currentApplicant?.sin && (
                    <p>
                      <strong>{t<string>('personalInformation.sin')} : </strong>
                      <span>{currentApplicant?.sin}</span>
                    </p>
                  )}
                </div>
                <button type="button" className="btn-action" onClick={() => gotoStep('personal-information')}>
                  {t<string>('review.modify')}
                </button>
              </div>
            </div>

            <div className="review-item">
              <h1 className="h3">{t<string>('common.yourHome')}</h1>
              <div className="box">
                <div className="box-content">
                  <p>
                    <strong>{t<string>('confirmation.address')} : </strong>
                    <span>
                      {currentApplicant?.currentAddress.apartment && `${currentApplicant?.currentAddress.apartment}-`}
                      {currentApplicant?.currentAddress.civicNumber} {currentApplicant?.currentAddress.street},{' '}
                      {currentApplicant?.currentAddress.city}, {currentApplicant?.currentAddress.stateIso}{' '}
                      {currentApplicant?.currentAddress.postalCode}
                    </span>
                  </p>
                  <p>
                    <strong>{t<string>('common.months')} : </strong>
                    <span>{t<string>('common.monthsLabel', { count: currentApplicant?.currentAddress.months })}</span>
                  </p>
                  <p>
                    <strong>{t('housing.homeFeeType')} : </strong>
                    <span>{t<string>(`enum.eHomeFeeType.${currentApplicant?.expenses.homeFeeTypeId}`)}</span>
                  </p>
                </div>
                <button type="button" className="btn-action" onClick={() => gotoStep('address')}>
                  {t<string>('review.modify')}
                </button>
              </div>
            </div>

            <div className="review-item">
              <h1 className="h3">{t('income.howMuch')}</h1>
              <div className="box">
                <div className="box-content">
                  {head(currentApplicant?.currentJobs) && (
                    <>
                      <p>
                        <strong>{t('income.category')} : </strong>
                        <span> {t<string>(`enum.eJobType.${first(currentApplicant?.currentJobs)?.jobType}`)}</span>
                      </p>
                      <p>
                        <strong>{t('income.jobTitle')} : </strong>
                        <span> {first(currentApplicant?.currentJobs)?.jobTitle}</span>
                      </p>
                      <p>
                        <strong>{t('income.beforeDeductions')} : </strong>
                        <span> {FormatCurrency(first(currentApplicant?.currentJobs)?.annualSalary)}</span>
                      </p>
                      <p>
                        <strong>{t('income.employerName')} : </strong>
                        <span> {first(currentApplicant?.currentJobs)?.employerName}</span>
                      </p>
                      <p>
                        <strong>{t('income.phoneNumber')} : </strong>
                        <span> {formatPhone(first(currentApplicant?.currentJobs)?.employerPhone)}</span>
                      </p>
                      <p>
                        <strong>{t('income.extension')} : </strong>
                        <span> {first(currentApplicant?.currentJobs)?.employerPhoneExt}</span>
                      </p>

                      <p>
                        <strong>{t('income.employmentLenght')} : </strong>
                        <span>
                          {t<string>('common.monthsLabel', {
                            count: first(currentApplicant?.currentJobs)?.months as number,
                          })}
                        </span>
                      </p>
                    </>
                  )}
                  <p>
                    <strong>{t('income.otherIncome')} : </strong>
                    <span> {first(currentApplicant?.otherIncomes)?.annualAmount}</span>
                  </p>
                </div>
                <button type="button" className="btn-action" onClick={() => gotoStep('employment')}>
                  {t<string>('review.modify')}
                </button>
              </div>
            </div>
            {shouldDisplayAnyPromoCodeRelatedInfo && (
              <div className="review-item">
                <h1 className="h3">{t('loanAmount.promoCode')}</h1>
                <div className="box" style={{ padding: '2.5rem 3rem 2.5rem 3rem' }}>
                  {shoudDisplayPromoCodeInput && (
                    <div
                      className="box-content"
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        flexWrap: 'wrap',
                      }}
                    >
                      <div className="control-group mid">
                        <label htmlFor="promoCode">{t<string>('loanAmount.promoCode')}</label>
                        <input
                          style={{ textTransform: 'uppercase' }}
                          id="promoCode"
                          type="text"
                          value={promoCodeInput}
                          onChange={(e) => onPromoCodeChange(e)}
                          disabled={isValidatingPromoCode || isPromoCodeValidated}
                        />
                        {getPromoError()}
                      </div>
                      <div className="control-group mid">
                        <button
                          disabled={promoCodeInput.length === 0 || isValidatingPromoCode || isPromoCodeValidated}
                          type="button"
                          className="btn btn-blue"
                          onClick={() => validatePromoCode(promoCodeInput)}
                        >
                          {getValidatePromotionButtonText()}
                        </button>
                      </div>
                    </div>
                  )}
                  {shouldDisplayPromoCodeReviewInfo && (
                    <p>
                      <strong>{t('loanAmount.validPromo')} : </strong>
                      <span>{promoCodeInput.toUpperCase()}</span>
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
          <footer className="review-footer">
            <h3>
              <Trans i18nKey={isCoapplicant ? 'prequalification.pullCreditReport' : 'confirmation.consentTitle'} />
            </h3>

            <div className="control-group">
              <input
                required
                type="checkbox"
                id="acceptConditions"
                onChange={(e) => {
                  if (e.currentTarget.checked === true) e.currentTarget.setCustomValidity('')
                }}
                onInvalid={(e) => e.currentTarget.setCustomValidity(t<string>('common.acceptConditions'))}
              />
              <label htmlFor="acceptConditions">
                {isCoapplicant ? <p> {t('prequalification.acceptConditions')} </p> : t<string>('confirmation.consent')}
              </label>
            </div>
            <div className="control-group">
              <input
                required
                type="checkbox"
                id="acceptAuthorise"
                onChange={(e) => {
                  if (e.currentTarget.checked === true) e.currentTarget.setCustomValidity('')
                }}
                onInvalid={(e) => e.currentTarget.setCustomValidity(t<string>('common.acceptAuthorization'))}
              />
              <label htmlFor="acceptAuthorise">
                {t<string>('confirmation.authorization')}
                <u>
                  <a href={t<string>('common.policyLink')} target="_blank" rel="noreferrer">
                    {t<string>('contact.linkText')}
                  </a>
                </u>
                {t<string>('confirmation.authorizationEnd')}
              </label>
            </div>
            <button type="submit" className="btn btn-blue" disabled={isSubmitted || isSubmitting}>
              {t<string>('review.getQuote')}
              {isSubmitting && <div className="mini-spinner" />}
            </button>
          </footer>
        </form>
      </section>
    </main>
  )
}

Review.defaultProps = {
  isCoapplicant: false,
}

export default Review
